import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { startTransition, useMemo } from 'react';
import { toast } from 'sonner';
import { XIcon } from 'lucide-react';
import { useMutation, useReadQuery, useSuspenseQuery } from '@apollo/client/react';

import { Button } from '@/components/ui/button';
import { EscrowMembershipRemoveDocument } from 'src/graphql/actions/escrowMemberships/escrowMembershipRemove.mutation';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { UserRoleEnum } from 'src/graphql/schema';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';

import { getMembershipSortPriority } from '../lib';
import { refetchUsers, refetchEscrowMemberships } from '../api';
import { useMembershipsManagement } from '../model';

import RoleCell from './RoleCell';
import PermissionTooltip from './PermissionTooltip';

type IMembershipRow = {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRoleEnum;
    isCurrentUser: boolean;
  };
  role: string;
  action: {
    canUpdate: boolean;
    canRemove: boolean;
  };
};

const RemoveCell = ({ id, canRemove }: { id: string; canRemove: boolean }) => {
  const onCompleted = () =>
    startTransition(() => {
      refetchUsers();
      refetchEscrowMemberships();
    });

  const [removeEscrowMembership, { loading }] = useMutation(EscrowMembershipRemoveDocument, {
    onCompleted,
  });

  const onRemove = async () => {
    try {
      await removeEscrowMembership({
        variables: {
          escrowMembershipId: id,
        },
      });
      toast.success('Member access has been removed');
    } catch (error) {
      toast.error('Failed to remove member access');
    }
  };

  if (canRemove)
    return (
      <Button variant="ghost" onClick={onRemove} disabled={loading} isLoading={loading} size="icon" className="w-7 h-7">
        <XIcon />
      </Button>
    );

  return null;
};

const MemberBadge = ({ role, isCurrentUser }: { role: UserRoleEnum; isCurrentUser: boolean }) => {
  const badgeClasses = 'border-0 text-2xs py-0.5 px-1.5 font-bold shrink-0';

  if (role === UserRoleEnum.Owner) {
    return (
      <Badge variant="pill" className={cn('whitespace-nowrap bg-foreground text-white', badgeClasses)}>
        Account owner
      </Badge>
    );
  }

  if (isCurrentUser)
    return (
      <Badge variant="pill" className={badgeClasses}>
        You
      </Badge>
    );

  return null;
};

const columnHelper = createColumnHelper<IMembershipRow>();
const columns = [
  columnHelper.accessor('user', {
    cell: (props) => {
      const { name, email, role, isCurrentUser } = props.getValue();
      const fallback = name.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-6.5 rounded-md shrink-0">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 text-sm text-foreground font-semibold truncate">{name}</div>
          <MemberBadge role={role} isCurrentUser={isCurrentUser} />
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    cell: (props) => (
      <RoleCell role={props.getValue()} id={props.row.original.id} canUpdate={props.row.original.action.canUpdate} />
    ),
  }),
  columnHelper.accessor('action', {
    cell: (props) => <RemoveCell id={props.row.original.id} canRemove={props.row.original.action.canRemove} />,
  }),
];

const AddedMemberships = () => {
  const { queryEscrowMembershipsRef } = useMembershipsManagement();
  const {
    data: { escrowMemberships },
  } = useReadQuery(queryEscrowMembershipsRef);
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);

  const membersMapped = useMemo(() => {
    const sortedNodes = escrowMemberships.nodes.slice().sort((a, b) => {
      const priorityA = getMembershipSortPriority(a.appSubscription.user, profile.id);
      const priorityB = getMembershipSortPriority(b.appSubscription.user, profile.id);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    return sortedNodes.map((membership) => ({
      id: membership.id,
      user: {
        id: membership.appSubscription.user.id,
        name: membership.appSubscription.user.name || '-',
        email: membership.appSubscription.user.email || '-',
        role: membership.appSubscription.user.role,
        isCurrentUser: membership.appSubscription.user.id === profile.id,
      },
      role: membership.role,
      action: {
        canUpdate: membership.policy.update,
        canRemove: membership.policy.remove,
      },
    }));
  }, [escrowMemberships.nodes, profile.id]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-2.5 overflow-hidden flex flex-col py-3.5 px-4">
      <h2 className="text-base font-extrabold">Escrow members ({escrowMemberships.nodesCount})</h2>
      <div className="rounded-md border border-border overflow-hidden grow">
        <Table maxHeight="max-h-[320px]" className="table-fixed">
          <colgroup>
            <col />
            <col className="w-30" />
            <col className="w-16" />
          </colgroup>
          <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:left-0 after:bottom-0 after:w-full after:border-b after:border-border">
            <TableRow>
              <TableHead className="h-8">Member</TableHead>
              <TableHead colSpan={2} className="h-8">
                <div className="flex justify-end items-center gap-2">
                  Permissions <PermissionTooltip />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const [userCell, roleCell, actionCell] = row.getVisibleCells();
              const isOwner = row.original.user.role === UserRoleEnum.Owner;

              return (
                <TableRow key={row.id} className="hover:bg-white">
                  <TableCell key={userCell.id} className="py-2.25">
                    {flexRender(userCell.column.columnDef.cell, userCell.getContext())}
                  </TableCell>
                  <TableCell key={roleCell.id} colSpan={isOwner ? 2 : 1} className="py-2.25">
                    {flexRender(roleCell.column.columnDef.cell, roleCell.getContext())}
                  </TableCell>
                  {!isOwner && (
                    <TableCell key={actionCell.id} className="py-2.25">
                      {flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default withSuspense(AddedMemberships);
